/**
 * Investment Advisor Chat Widget
 *
 * Floating chat widget for personalized investment advice powered by AI.
 * - Has access to complete portfolio data
 * - Provides investment advice, calculations, and analysis
 * - Uses Claude with Grok and ChatGPT fallbacks
 * - Only available for authenticated users
 *
 * On large screens (>= 1400px): displays as a fixed sidebar on the right
 * On smaller screens: displays as a floating popup
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Icons } from '../Icons';
import { SafeHTML } from '../common/SafeHTML';
import { PortfolioContextService, type PortfolioContext } from '../../services/portfolioContextService';
import { useAuthStore } from '../../store/authStore';
import type { Token, Prices, FIFOResults } from '../../types';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface InvestmentAdvisorChatProps {
  tokens: Token[];
  prices: Prices;
  fifoResults: FIFOResults;
  calculationMethod?: 'FIFO' | 'LIFO' | 'AVERAGE_COST';
}

// Sidebar width for large screens
const SIDEBAR_WIDTH = 400;
const LARGE_SCREEN_BREAKPOINT = 1400; // ~14 inch screens
const TOPBAR_HEIGHT = 72; // Match the app's top bar height

// Generate unique session ID
function getSessionId(): string {
  const key = 'advisor_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = `adv_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
}

// Get browser language
function getBrowserLanguage(): 'fr' | 'en' {
  const lang = navigator.language || 'en';
  return lang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

// Suggested questions
const SUGGESTED_QUESTIONS = {
  fr: [
    'Quel est mon token le plus rentable ?',
    'Analyse mon risque de concentration',
    'Si BTC monte de 20%, quel sera l\'impact ?',
    'Dois-je rééquilibrer mon portefeuille ?',
    'Calcule mon prix moyen d\'achat pour ETH',
    'Quels tokens sont en perte ?',
  ],
  en: [
    'What is my most profitable token?',
    'Analyze my concentration risk',
    'If BTC rises 20%, what will be the impact?',
    'Should I rebalance my portfolio?',
    'Calculate my average buy price for ETH',
    'Which tokens are at a loss?',
  ],
};

export function InvestmentAdvisorChat({
  tokens,
  prices,
  fifoResults,
  calculationMethod = 'FIFO',
}: InvestmentAdvisorChatProps) {
  // Check authentication - only show for logged in users
  const { isAuthenticated } = useAuthStore();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Large screen detection for sidebar mode
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sessionIdRef = useRef<string>(getSessionId());
  const language = getBrowserLanguage();

  // Detect large screens
  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= LARGE_SCREEN_BREAKPOINT);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Add/remove body class for sidebar mode (to push main content)
  useEffect(() => {
    const shouldShowSidebar = isOpen && isLargeScreen;

    if (shouldShowSidebar) {
      document.body.classList.add('advisor-sidebar-open');
      document.body.style.marginRight = `${SIDEBAR_WIDTH}px`;
      document.body.style.transition = 'margin-right 0.3s ease';
    } else {
      document.body.classList.remove('advisor-sidebar-open');
      document.body.style.marginRight = '';
    }

    return () => {
      document.body.classList.remove('advisor-sidebar-open');
      document.body.style.marginRight = '';
    };
  }, [isOpen, isLargeScreen]);

  // Build portfolio context
  const portfolioContext: PortfolioContext = useMemo(() => {
    return PortfolioContextService.buildContext(tokens, prices, fifoResults, calculationMethod);
  }, [tokens, prices, fifoResults, calculationMethod]);

  // Get compact summary for display
  const portfolioSummary = useMemo(() => {
    return PortfolioContextService.getCompactSummary(portfolioContext);
  }, [portfolioContext]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Welcome message
  const getWelcomeMessage = (): string => {
    if (language === 'fr') {
      return `Bonjour ! Je suis votre conseiller en investissement IA.

J'ai accès à votre portefeuille complet et je peux vous aider avec :
- Analyse de performance et P/L
- Calculs de scénarios ("si BTC monte de 20%...")
- Conseils de rééquilibrage
- Identification des risques

**${portfolioSummary}**

Posez-moi vos questions !`;
    } else {
      return `Hello! I'm your AI investment advisor.

I have access to your complete portfolio and can help you with:
- Performance and P/L analysis
- Scenario calculations ("if BTC rises 20%...")
- Rebalancing advice
- Risk identification

**${portfolioSummary}**

Ask me your questions!`;
    }
  };

  // Initialize welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: getWelcomeMessage(),
        timestamp: new Date(),
      }]);
    }
  }, [isOpen, messages.length, portfolioSummary]);

  // Send message
  const sendMessage = useCallback(async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading) return;

    setError(null);
    setInputValue('');
    setShowSuggestions(false);

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await fetch('/api/advisor/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: messages.map(m => ({ role: m.role, content: m.content })),
          portfolioContext,
          language,
          sessionId: sessionIdRef.current,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur de communication');
      }

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.message,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, messages, portfolioContext, language]);

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (question: string) => {
    sendMessage(question);
  };

  // Format message content with markdown-like styling
  const formatContent = (content: string) => {
    // Bold text
    let formatted = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Line breaks
    formatted = formatted.replace(/\n/g, '<br>');
    return formatted;
  };

  // Determine if we should show sidebar mode
  const isSidebarMode = isLargeScreen;
  const showAsSidebar = isOpen && isSidebarMode;

  // Don't render anything if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Floating Button - Edouard AI style - visible when chat is closed OR in popup mode */}
      {(!isOpen || !isSidebarMode) && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          data-advisor-chat="true"
          style={{
            position: 'fixed',
            bottom: '96px',
            right: '24px',
            padding: isOpen ? '14px' : '12px 20px',
            borderRadius: isOpen ? '50%' : '16px',
            background: isOpen
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 100%)',
            border: isOpen ? 'none' : '1px solid rgba(139, 92, 246, 0.3)',
            boxShadow: isOpen
              ? '0 4px 20px rgba(16, 185, 129, 0.4)'
              : '0 4px 24px rgba(139, 92, 246, 0.25)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            transition: 'all 0.3s ease',
            zIndex: 9996,
            backdropFilter: 'blur(10px)',
          }}
          onMouseEnter={e => {
            if (isOpen) {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 25px rgba(16, 185, 129, 0.5)';
            } else {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(139, 92, 246, 0.35)';
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.25) 0%, rgba(139, 92, 246, 0.1) 100%)';
            }
          }}
          onMouseLeave={e => {
            if (isOpen) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(16, 185, 129, 0.4)';
            } else {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 24px rgba(139, 92, 246, 0.25)';
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 100%)';
            }
          }}
          aria-label={isOpen ? 'Fermer le conseiller' : 'Ouvrir le conseiller investissement'}
        >
          {isOpen ? (
            <Icons.X size={22} color="white" />
          ) : (
            <>
              {/* Sparkles icon */}
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.4)',
                }}
              >
                <Icons.Sparkles size={18} color="white" />
              </div>
              {/* Text content */}
              <div style={{ textAlign: 'left' }}>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'var(--text-primary, #fff)',
                    lineHeight: 1.2,
                  }}
                >
                  Edouard AI
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: 'var(--text-secondary, rgba(255,255,255,0.7))',
                    lineHeight: 1.2,
                  }}
                >
                  {language === 'fr' ? 'Conseiller IA' : 'AI Advisor'}
                </div>
              </div>
              {/* Animated pulse indicator */}
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#10b981',
                  boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.3)',
                  animation: 'pulse-glow 2s ease-in-out infinite',
                  marginLeft: '4px',
                }}
              />
              <style>{`
                @keyframes pulse-glow {
                  0%, 100% { box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.3); }
                  50% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.1); }
                }
              `}</style>
            </>
          )}
        </button>
      )}

      {/* Chat Panel - Sidebar mode for large screens, Popup for smaller screens */}
      {isOpen && (
        <div
          data-advisor-chat="true"
          style={showAsSidebar ? {
            // SIDEBAR MODE (large screens)
            position: 'fixed',
            top: 0,
            right: 0,
            width: `${SIDEBAR_WIDTH}px`,
            height: '100vh',
            background: 'var(--card-bg, #1a1a2e)',
            boxShadow: '-4px 0 30px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 9997,
            borderLeft: '1px solid var(--border, rgba(255,255,255,0.1))',
          } : {
            // POPUP MODE (smaller screens)
            position: 'fixed',
            bottom: '168px',
            right: '24px',
            width: '400px',
            maxWidth: 'calc(100vw - 48px)',
            height: '550px',
            maxHeight: 'calc(100vh - 200px)',
            background: 'var(--card-bg, #1a1a2e)',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 9997,
            border: '1px solid var(--border, rgba(255,255,255,0.1))',
          }}
        >
          {/* Header - Edouard AI branding style */}
          <div
            style={{
              height: showAsSidebar ? `${TOPBAR_HEIGHT}px` : 'auto',
              padding: showAsSidebar ? '0 20px' : '16px 20px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexShrink: 0,
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icons.Sparkles size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                color: 'white',
                fontWeight: 600,
                fontSize: showAsSidebar ? '16px' : '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                Edouard AI
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 500,
                    padding: '2px 6px',
                    background: 'rgba(16, 185, 129, 0.3)',
                    borderRadius: '4px',
                    color: '#10b981',
                  }}
                >
                  BETA
                </span>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                {language === 'fr' ? 'Conseiller IA personnel' : 'Personal AI Advisor'}
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                borderRadius: '10px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              aria-label="Fermer"
            >
              <Icons.X size={18} color="white" />
            </button>
          </div>

          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: showAsSidebar ? '20px' : '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: showAsSidebar ? '16px' : '12px',
            }}
          >
            {messages.map(message => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <SafeHTML
                  profile="chat"
                  html={formatContent(message.content)}
                  style={{
                    maxWidth: '85%',
                    padding: showAsSidebar ? '14px 18px' : '12px 16px',
                    borderRadius: message.role === 'user'
                      ? '16px 16px 4px 16px'
                      : '16px 16px 16px 4px',
                    background: message.role === 'user'
                      ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                      : 'var(--hover-bg, rgba(255,255,255,0.05))',
                    color: message.role === 'user' ? 'white' : 'var(--text-primary, #e0e0e0)',
                    fontSize: showAsSidebar ? '15px' : '14px',
                    lineHeight: '1.6',
                  }}
                />
              </div>
            ))}

            {/* Suggested Questions */}
            {showSuggestions && messages.length === 1 && (
              <div style={{ marginTop: '8px' }}>
                <div style={{
                  fontSize: showAsSidebar ? '13px' : '12px',
                  color: 'var(--text-secondary, #888)',
                  marginBottom: '10px',
                }}>
                  {language === 'fr' ? 'Questions suggérées :' : 'Suggested questions:'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {SUGGESTED_QUESTIONS[language].slice(0, showAsSidebar ? 6 : 4).map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(question)}
                      style={{
                        padding: showAsSidebar ? '10px 14px' : '8px 12px',
                        borderRadius: '20px',
                        border: '1px solid rgba(139, 92, 246, 0.3)',
                        background: 'rgba(139, 92, 246, 0.1)',
                        color: '#a78bfa',
                        fontSize: showAsSidebar ? '13px' : '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                      }}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Loading indicator */}
            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    padding: showAsSidebar ? '14px 18px' : '12px 16px',
                    borderRadius: '16px 16px 16px 4px',
                    background: 'var(--hover-bg, rgba(255,255,255,0.05))',
                    display: 'flex',
                    gap: '6px',
                  }}
                >
                  <span className="typing-dot" style={{ animationDelay: '0s' }}>•</span>
                  <span className="typing-dot" style={{ animationDelay: '0.2s' }}>•</span>
                  <span className="typing-dot" style={{ animationDelay: '0.4s' }}>•</span>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  fontSize: '13px',
                  textAlign: 'center',
                }}
              >
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Portfolio Summary Bar */}
          <div
            style={{
              padding: showAsSidebar ? '10px 20px' : '8px 16px',
              background: 'rgba(16, 185, 129, 0.1)',
              borderTop: '1px solid var(--border, rgba(255,255,255,0.1))',
              fontSize: showAsSidebar ? '12px' : '11px',
              color: 'var(--text-secondary, #888)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
            }}
          >
            <Icons.Briefcase size={showAsSidebar ? 16 : 14} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {portfolioSummary}
            </span>
          </div>

          {/* Input Area */}
          <div
            style={{
              padding: showAsSidebar ? '16px 20px' : '12px 16px',
              borderTop: '1px solid var(--border, rgba(255,255,255,0.1))',
              display: 'flex',
              gap: showAsSidebar ? '12px' : '10px',
              alignItems: 'flex-end',
              flexShrink: 0,
            }}
          >
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={language === 'fr' ? 'Posez votre question...' : 'Ask your question...'}
              disabled={isLoading}
              rows={1}
              style={{
                flex: 1,
                padding: showAsSidebar ? '14px 16px' : '12px 14px',
                borderRadius: '12px',
                border: '1px solid var(--border, rgba(255,255,255,0.1))',
                background: 'var(--input-bg, rgba(255,255,255,0.05))',
                color: 'var(--text-primary, #e0e0e0)',
                fontSize: showAsSidebar ? '15px' : '14px',
                resize: 'none',
                maxHeight: showAsSidebar ? '150px' : '120px',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              onInput={e => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, showAsSidebar ? 150 : 120) + 'px';
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!inputValue.trim() || isLoading}
              style={{
                width: showAsSidebar ? '50px' : '44px',
                height: showAsSidebar ? '50px' : '44px',
                borderRadius: '12px',
                border: 'none',
                background: inputValue.trim() && !isLoading
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                  : 'var(--hover-bg, rgba(255,255,255,0.1))',
                cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                flexShrink: 0,
                boxShadow: inputValue.trim() && !isLoading
                  ? '0 4px 12px rgba(139, 92, 246, 0.3)'
                  : 'none',
              }}
              aria-label="Envoyer"
            >
              <Icons.Send
                size={showAsSidebar ? 22 : 20}
                color={inputValue.trim() && !isLoading ? 'white' : 'var(--text-secondary, #888)'}
              />
            </button>
          </div>
        </div>
      )}

      {/* Typing animation styles */}
      <style>{`
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        .typing-dot {
          display: inline-block;
          color: var(--text-secondary, #888);
          animation: typingBounce 1.4s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}
